import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { toInteger } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { eramanCMSApi, post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { fields } from "../constants";
import { useProductsDocumentDetails } from "./Products.hook";

export const AddEditProducts = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [storeDropdownOptions, setStoreDropdownOptions] = useState<{ label: string; value: string }[]>([]);
  const [airportDropdownOptions, setAirportDropdownOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoriesData, setCategoriesData] = useState<any[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<string | number | null>(null);
  const [subCategoriesData, setSubCategoriesData] = useState<any[]>([]);
  const [brandData, setBrandData] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const fetchAirportDropdownData = async () => {
    try {
      const response = await eramanCMSApi.get(`/api/airport-dropdown`);
      const data = response.data || [];
      const options = data.map((item: { id: string; name: string }) => ({
        label: decodeHTML(item.name),
        value: item.name,
      }));
      setAirportDropdownOptions(options);
    } catch (error) {
      console.error("Failed to fetch airport dropdown:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await eramanCMSApi.get(`/api/get-categories`);
      if (res?.data) {
        const allowedCategories = [73, 74, 75];
        const data = res.data
          .filter((item: any) => allowedCategories.includes(item.tid))
          .map((item: any) => ({
            label: decodeHTML(item.field_category_name),
            value: item.tid,
          }));
        setCategoriesData(data);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategoriesData([]);
    }
  };
  const fetchStoreDropdownData = async () => {
    try {
      const response = await eramanCMSApi.get(`/api/store-location-dropdown`);
      const data = response.data?.data || [];
      const options = data.map((item: { id: string; name: string }) => ({
        label: item.name,
        value: item.id,
      }));
      setStoreDropdownOptions(options);
      console.log(storeDropdownOptions, "storeDropdownOptions");
    } catch (error) {
      console.error("Failed to fetch store dropdown:", error);
    }
  };

  useEffect(() => {
    let categoryId = (selectedCategory as any)?.value || selectedCategory;

    // If categoryId is a string (category name), find the corresponding tid
    if (typeof categoryId === "string" && categoriesData.length > 0) {
      const matchedCategory = categoriesData.find((cat) => cat.label === categoryId);
      categoryId = matchedCategory?.value || categoryId;
    }

    if (!categoryId) return;
    const fetchSubCategories = async () => {
      try {
        const res = await eramanCMSApi.get(`/api/get-all-subcategories/${categoryId}`);
        if (res?.data) {
          const data = res.data.map((item: any) => ({
            label: decodeHTML(item.name),
            value: item.tid,
          }));
          setSubCategoriesData(data);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubCategoriesData([]);
      }
    };

    const fetchBrands = async () => {
      try {
        const res = await eramanCMSApi.get(`/api/all-brands/all-fields/published/${categoryId}`);
        if (res?.data) {
          const data = res.data.map((item: any) => ({
            label: decodeHTML(item.name),
            value: item.id,
          }));
          setBrandData(data);
        }
      } catch (err) {
        console.error("Error fetching brands:", err);
        setBrandData([]);
      }
    };
    fetchSubCategories();
    fetchBrands();
  }, [selectedCategory, categoriesData]);
  const getRecords = () => {
    const url = `/api/all-products/all/all/all`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setRecords(envelopes);
        }
      })
      .catch((err) => {
        setRecords([]);
      });
  };

  useEffect(() => {
    fetchAirportDropdownData();
    fetchCategories();
    fetchStoreDropdownData();
    getRecords();
  }, []);

  const {
    data: editProducts,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = useProductsDocumentDetails(query.get("editId") || "");

  const {
    data: editProductsById,
    isLoading: isLoadingProducts,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("editId"), query.get("id")]);
  const handleCategoryChange = useCallback(
    (value: string | number | { label: string; value: string | number }) => {
      const categoryValue = typeof value === "object" ? value.value : value;
      if (categoryValue !== selectedCategory) {
        setSelectedCategory(categoryValue);
      }
    },
    [selectedCategory]
  );

  const initialValues = useMemo(
    () => ({
      image: editProductsById?.payload
        ? Array.isArray(editProductsById?.payload?.image)
          ? editProductsById?.payload?.image?.map((item: any, index: number) => {
              const imageUrl = typeof item === "string" ? item : item?.url || "";
              return {
                uid: `${index + 1}`,
                name: imageUrl?.trim().split("/").pop() || `image-${index + 1}.png`,
                status: "done",
                url: imageUrl?.trim(),
              };
            })
          : []
        : Array.isArray(editProducts?.image)
          ? editProducts?.image.map((item: any, index: number) => {
              const imageUrl = typeof item === "string" ? item : item?.url || "";
              return {
                uid: `${index + 1}`,
                name: imageUrl?.trim().split("/").pop() || `image-${index + 1}.png`,
                status: "done",
                url: imageUrl?.trim(),
              };
            })
          : [],
      title: decodeHTML(editProductsById?.payload?.title || editProducts?.title),
      field_common_malay_title: decodeHTML(
        editProductsById?.payload?.field_common_malay_title || editProducts?.field_common_malay_title || ""
      ),
      field_common_mandarin_title: decodeHTML(
        editProductsById?.payload?.field_common_mandarin_title || editProducts?.field_common_mandarin_title || ""
      ),
      body: decodeHTML(editProductsById?.payload?.body || editProducts?.body),
      field_common_malay_description: decodeHTML(
        editProductsById?.payload?.field_common_malay_description || editProducts?.field_common_malay_description || ""
      ),
      field_common_mandarin_descriptio: decodeHTML(
        editProductsById?.payload?.field_common_mandarin_descriptio ||
          editProducts?.field_common_mandarin_descriptio ||
          ""
      ),

      category: (() => {
        const raw = editProductsById?.payload?.category || editProducts?.category;
        const decoded = decodeHTML(raw);
        const matched = categoriesData?.find((opt) => opt?.value === decoded || opt?.label === decoded);
        return decodeHTML(matched?.label || decoded || "");
      })(),
      subCategory: decodeHTML(editProductsById?.payload?.subCategory || editProducts?.subCategory || ""),

      brand: decodeHTML(editProductsById?.payload?.brand || editProducts?.brand || ""),

      productCode: decodeHTML(editProductsById?.payload?.productCode || editProducts?.productCode),
      dutyFree: editProductsById?.payload?.dutyFree ?? editProducts?.dutyFree,
      oldPrice: decodeHTML(editProductsById?.payload?.oldPrice || editProducts?.oldPrice),
      price: decodeHTML(editProductsById?.payload?.price || editProducts?.price),
      productOffer: decodeHTML(editProductsById?.payload?.productOffer || editProducts?.productOffer),
      field_new_arrival: editProductsById?.payload?.field_new_arrival ?? editProducts?.field_new_arrival ?? false,

      field_airport_export: decodeHTML(
        editProductsById?.payload?.field_airport_export || editProducts?.field_airport_export || []
      ),
      field_store_location_export: decodeHTML(
        editProductsById?.payload?.field_store_location_export || editProducts?.field_store_location_export || []
      ),

      field_promotion_title: decodeHTML(
        editProductsById?.payload?.field_promotion_title || editProducts?.field_promotion_title || ""
      ),
      field_malay_promotion_title: decodeHTML(
        editProductsById?.payload?.field_malay_promotion_title || editProducts?.field_malay_promotion_title || ""
      ),
      field_mandarin_promotion_title: decodeHTML(
        editProductsById?.payload?.field_mandarin_promotion_title || editProducts?.field_mandarin_promotion_title || ""
      ),
      field_best_seller: editProductsById?.payload?.field_best_seller ?? editProducts?.field_best_seller ?? false,
      field_promotions: editProductsById?.payload?.field_promotions ?? editProducts?.field_promotions ?? false,

      field_product_terms_conditions: decodeHTML(
        editProductsById?.payload?.field_product_terms_conditions || editProducts?.field_product_terms_conditions || ""
      ),
      field_malay_terms_and_conditions: decodeHTML(
        editProductsById?.payload?.field_malay_terms_and_conditions ||
          editProducts?.field_malay_terms_and_conditions ||
          ""
      ),
      field_mandarin_terms_and_conditi: decodeHTML(
        editProductsById?.payload?.field_mandarin_terms_and_conditi ||
          editProducts?.field_mandarin_terms_and_conditi ||
          ""
      ),
      status: editProductsById?.payload?.status || editProducts?.status,
    }),
    [editProductsById, editProducts, categoriesData]
  );

  const formFields = useMemo(() => {
    return fields?.products?.map((field: any) => {
      // if (field.name === "field_store_location_export") {
      //   return {
      //     ...field,
      //     options: storeDropdownOptions,
      //   };
      // }
      if (field?.name === "field_airport_export") {
        return {
          ...field,
          options: airportDropdownOptions,
        };
      }

      if (field?.name === "category") {
        return {
          ...field,
          options: categoriesData,
          onChange: handleCategoryChange,
        };
      }

      if (field?.name === "subCategory") {
        return {
          ...field,
          options: subCategoriesData,
        };
      }

      if (field?.name === "brand") {
        return {
          ...field,
          options: brandData,
        };
      }
      if (field?.name === "productCode") {
        return {
          ...field,
          validation: {
            required: `${field.label || field.name} is required`,
            validate: (value: string, formValues: FieldValues) => {
              if (!value) return true;
              const existingRecord = records?.find(
                (record: any) =>
                  toInteger(record?.productCode) === toInteger(value) &&
                  record.id !== (editProducts?.id || editProductsById?.payload?.id)
              );
              return !existingRecord || `${field.label || field.name} already exists`;
            },
          },
        };
      }

      return field;
    });
  }, [
    storeDropdownOptions,
    airportDropdownOptions,
    categoriesData,
    handleCategoryChange,
    subCategoriesData,
    brandData,
    records,
  ]);

  const productFormConfig: FormConfig = useMemo(
    () => ({
      initialValues,
      fields: formFields,
    }),
    [initialValues, formFields]
  );
  useEffect(() => {
    const initialCategory = editProductsById?.payload?.category || editProducts?.category;
    if (initialCategory && categoriesData?.length > 0) {
      const decodedCategory = decodeHTML(initialCategory);
      // Try to find the category by tid first, then by name
      const matchedCategory = categoriesData?.find(
        (cat) => cat.value === decodedCategory || cat?.label === decodedCategory
      );
      setSelectedCategory(matchedCategory || decodedCategory);
    }
  }, [editProductsById?.payload?.category, editProducts?.category, categoriesData]);

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    console.log("data-123", data);
    let imageKeys: any = ["image"];
    for (const key of imageKeys) {
      try {
        const field = data[key];
        if (!Array.isArray(field)) continue;

        const processedUrls: string[] = [];

        for (const file of field) {
          if (file.originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", file.originFileObj);
            formData.append("application", "ERAMAN");

            const res: any = await post("management/upload-file", formData);
            if (res?.response?.data) {
              processedUrls.push(res.response.data);
            }
          } else if (file.url) {
            processedUrls.push(file.url);
          }
        }

        data[key] = processedUrls;
      } catch (error) {
        console.error("error");
      }
    }
    if (data.category) {
      const categoryValue = typeof data.category === "object" ? data.category.value : data.category;
      const matchedCategory = categoriesData.find((cat) => cat.value === categoryValue || cat.label === categoryValue);
      data.category = matchedCategory?.label || categoryValue || "";
    }

    if (data.subCategory) {
      const subCategoryValue = typeof data.subCategory === "object" ? data.subCategory.value : data.subCategory;
      const matchedSubCategory = subCategoriesData.find(
        (sub) => sub.value === subCategoryValue || sub.label === subCategoryValue
      );
      data.subCategory = matchedSubCategory?.label || subCategoryValue || "";
    }

    if (data.brand) {
      const brandValue = typeof data.brand === "object" ? data.brand.value : data.brand;
      const matchedBrand = brandData.find((brand) => brand.value === brandValue || brand.label === brandValue);
      data.brand = matchedBrand?.label || brandValue || "";
    }

    // if (data.field_store_location_export) {
    //   data.field_store_location_export = Array.isArray(data.field_store_location_export)
    //     ? data.field_store_location_export.map((id: string) => {
    //         const matchedStore = storeDropdownOptions.find((opt) => opt.value === id || opt.label === id);
    //         return matchedStore?.label || id;
    //       })
    //     : data.field_store_location_export;
    // }

    const payload = {
      action: action,
      method: method,
      module: "Product Management",
      subModule: "products",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        // field_sub_created_at: "2023-11-21T10:01:09",
        // field_sub_updated_at: "2025-11-29T10:01:09"
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingProducts}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Products`}
      showBack
    >
      <DynamicForm
        key="product-form"
        isLoading={isLoadingProducts || drilldownLoading}
        config={productFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${eramanHost}/api/products/${editProducts?.id || editProductsById?.payload?.id}` : `${eramanHost}/api/products/new`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
